import React, { useEffect, useRef, useState } from 'react';
import { Header } from '@/components/shared';
import { useCalendarRoster } from './hooks/useCalendarRoster';
import { useCalendarData, usePrefetchCalendar } from './hooks/useCalendarData';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarGrid } from './CalendarGrid';
import { DayPanel } from './DayPanel';
import { AthletePicker } from './AthletePicker';
import { EmptyAthleteCTA } from './EmptyAthleteCTA';
import { AthleteSummaryStrip } from './AthleteSummaryStrip';
import {
  loadEnabledSources, saveEnabledSources, pushRecentAthlete,
} from './utils/storage';
import type { SourceKey } from './types';

function startOfMonth(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}
/** Inclusive last day of the month (e.g. May → 2026-05-31). */
function endOfMonth(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}
function startOfWeek(d: Date): string {
  const n = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  n.setUTCDate(n.getUTCDate() - n.getUTCDay());
  return n.toISOString().slice(0, 10);
}
/** Inclusive last day of the week — Saturday (e.g. week of May 10 → 2026-05-16). */
function endOfWeek(d: Date): string {
  const n = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  n.setUTCDate(n.getUTCDate() - n.getUTCDay() + 6);
  return n.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const SOURCE_KEY_ORDER: SourceKey[] = [
  'garmin', 'whoop', 'catapult', 'hawkin', 'coros', 'suunto', 'oura',
];

const CalendarPage: React.FC = () => {
  const userInfoStr = localStorage.getItem('user_info');
  const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
  const isAthlete = userInfo?.role === 'athlete';

  const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(
    isAthlete ? userInfo.id : null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useState<'month' | 'week'>('month');
  const [cursor, setCursor] = useState(() => new Date());
  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [enabledSources, setEnabledSources] = useState<SourceKey[]>(() => loadEnabledSources());

  const fromDate = view === 'week' ? startOfWeek(cursor) : startOfMonth(cursor);
  const toDate   = view === 'week' ? endOfWeek(cursor)   : endOfMonth(cursor);
  const today = todayIso();

  const roster = useCalendarRoster(!isAthlete);
  const data = useCalendarData(selectedAthleteId, fromDate, toDate);
  const prefetchCalendar = usePrefetchCalendar();

  const stepCursor = (direction: -1 | 1): Date => {
    const n = new Date(cursor);
    if (view === 'week') n.setUTCDate(n.getUTCDate() + direction * 7);
    else n.setUTCMonth(n.getUTCMonth() + direction);
    return n;
  };

  const prefetchAdjacent = (direction: -1 | 1) => {
    if (!selectedAthleteId) return;
    const adjacent = stepCursor(direction);
    const from = view === 'week' ? startOfWeek(adjacent) : startOfMonth(adjacent);
    const to   = view === 'week' ? endOfWeek(adjacent)   : endOfMonth(adjacent);
    prefetchCalendar(selectedAthleteId, from, to);
  };

  useEffect(() => saveEnabledSources(enabledSources), [enabledSources]);

  const toggleSource = (s: SourceKey) => {
    setEnabledSources((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  // Auto-open picker once on mount when no athlete is selected.
  const autoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (autoOpenFiredRef.current) return;
    if (!isAthlete && !selectedAthleteId) {
      autoOpenFiredRef.current = true;
      setPickerOpen(true);
    }
  }, [isAthlete, selectedAthleteId]);

  const handlePickAthlete = (id: number) => {
    pushRecentAthlete(id);
    setSelectedAthleteId(id);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !isAthlete) {
        e.preventDefault();
        setPickerOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        if (openDay) setOpenDay(null);
        else if (pickerOpen) setPickerOpen(false);
        return;
      }
      if (e.key.toLowerCase() === 't') {
        setCursor(new Date());
        setFocusedDate(new Date().toISOString().slice(0, 10));
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '[') {
        setCursor(stepCursor(-1));
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === ']') {
        setCursor(stepCursor(1));
        return;
      }
      if (['1', '2', '3', '4', '5', '6', '7'].includes(e.key)) {
        const idx = Number(e.key) - 1;
        toggleSource(SOURCE_KEY_ORDER[idx]);
        return;
      }

      const arrowDelta: Record<string, number> = {
        ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
      };
      if (e.key in arrowDelta) {
        e.preventDefault();
        setFocusedDate((current) => {
          const base = current
            ? new Date(current + 'T00:00:00Z')
            : new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
          base.setUTCDate(base.getUTCDate() + arrowDelta[e.key]);
          if (base.getUTCMonth() !== cursor.getUTCMonth()
              || base.getUTCFullYear() !== cursor.getUTCFullYear()) {
            setCursor(new Date(base));
          }
          return base.toISOString().slice(0, 10);
        });
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && focusedDate) {
        e.preventDefault();
        setOpenDay(focusedDate);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAthlete, openDay, pickerOpen, focusedDate, cursor]);

  return (
    <div className="h-screen flex flex-col cgm-bg-primary overflow-hidden">
      <Header userInfo={userInfo} />
      <CalendarToolbar
        fromDate={fromDate}
        toDate={toDate}
        view={view}
        onViewChange={setView}
        onPrev={() => setCursor(stepCursor(-1))}
        onNext={() => setCursor(stepCursor(1))}
        onToday={() => setCursor(new Date())}
        onOpenPicker={!isAthlete ? () => setPickerOpen(true) : undefined}
        freshness={data.data?.data_freshness ?? null}
        onPrefetchPrev={() => prefetchAdjacent(-1)}
        onPrefetchNext={() => prefetchAdjacent(1)}
      />
      {selectedAthleteId ? (
        <div className="flex flex-col flex-1 min-h-0">
          <AthleteSummaryStrip
            athlete={data.data?.athlete ?? null}
            summary={data.data?.summary ?? null}
            freshness={data.data?.data_freshness ?? null}
            days={data.data?.days ?? null}
            fromDate={fromDate}
            toDate={toDate}
            enabledSources={enabledSources}
            onToggleSource={toggleSource}
          />
          <div className="flex-1 min-h-0 overflow-hidden">
            <CalendarGrid
              payload={data.data}
              view={view}
              enabledSources={enabledSources}
              fromDate={fromDate}
              toDate={toDate}
              today={today}
              focusedDate={focusedDate ?? openDay}
              onSelectDay={(d) => {
                setFocusedDate(d);
                setOpenDay(d);
              }}
            />
          </div>
        </div>
      ) : (
        <EmptyAthleteCTA
          athletes={roster.data ?? []}
          onOpenPicker={() => setPickerOpen(true)}
          onPick={handlePickAthlete}
        />
      )}
      {openDay && data.data?.days[openDay] && (
        <DayPanel date={openDay} day={data.data.days[openDay]} onClose={() => setOpenDay(null)} />
      )}
      {pickerOpen && roster.data && (
        <AthletePicker
          athletes={roster.data}
          onPick={handlePickAthlete}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
